import { FC, useCallback, useState } from 'react';
import { PolicyInsight, PolicyInsightsConfig } from '../../types/policies';
import { Badge, EmptyState, EmptyStateIcons, EmptyStateProps, Icon, Icons, showAlert } from '@silverfort/sf-components';
import InsightsTopBar, { InsightsTopBarProps } from './components/InsightsTopBar';
import InsightsList from './components/InsightsList';
import { usePolicyInsightsConfig, useResolveInsight, useUnresolveInsight } from './api';
import PolicyInsightsLoading from './components/PolicyInsightsLoading';
import PolicyInsightsErrorPlaceholder from './components/PolicyInsightsErrorPlaceholder';
import { useGetUserData, UserSession } from '../../api';
import { isUserPermitted } from '../../TowerMenu/TowerMenuUtils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';

interface InsightsLayoutProps {
    insights: PolicyInsight[];
    insightsConfig: PolicyInsightsConfig;
    userSession: UserSession;
    onInsightsUpdate?: (insightId: PolicyInsight['id']) => void;
}

interface Props {
    insights: PolicyInsight[];
    showLoading?: boolean;
    showError?: boolean;
    onInsightsUpdate?: (insightId: PolicyInsight['id']) => void;
}

type StatusOption = 'unresolved' | 'resolved';

const OPEN_INSIGHTS_EMPTY_STATE_PROPS: EmptyStateProps = {
    title: 'You are all caught up, great job!',
    icon: EmptyStateIcons.TaskDone
};

const RESOLVED_INSIGHTS_EMPTY_STATE_PROPS: EmptyStateProps = {
    title: 'No resolved insights',
    icon: EmptyStateIcons.NoItems
};

const InsightsLayout: FC<InsightsLayoutProps> = ({ insights, insightsConfig, userSession, onInsightsUpdate }) => {
    const [selectedTopBarOption, setSelectedTopBarOption] = useState<StatusOption>('unresolved');

    const canEditPolicies = isUserPermitted(userSession, 'policies', ROLES.EDITOR.value);

    const resolvedInsights = insights.filter((insight) => insight.resolved);
    const unresolvedInsights = insights.filter((insight) => !insight.resolved);
    const insightsToDisplay = selectedTopBarOption === 'unresolved' ? unresolvedInsights : resolvedInsights;

    const { mutate: resolveInsight } = useResolveInsight({
        onSuccess: useCallback(
            ({ insightId }) => {
                onInsightsUpdate?.(insightId);
            },
            [onInsightsUpdate]
        ),
        onError: useCallback(() => {
            showAlert('Failed to resolve insight', 'error');
        }, [showAlert])
    });
    const { mutate: unresolveInsight } = useUnresolveInsight({
        onSuccess: useCallback(
            ({ insightId }) => {
                onInsightsUpdate?.(insightId);
            },
            [onInsightsUpdate]
        ),
        onError: useCallback(() => {
            showAlert('Failed to re-open insight', 'error');
        }, [showAlert])
    });

    const topBarOptions: InsightsTopBarProps<StatusOption>['options'] = [
        {
            id: 'unresolved',
            title: 'Open',
            amount: unresolvedInsights.length,
            leftProgressBarClassName: selectedTopBarOption === 'unresolved' ? 'bg-sf-primary-500' : 'bg-sf-primary-100',
            rightProgressBarClassName: selectedTopBarOption === 'unresolved' ? 'bg-sf-gray-400' : 'bg-sf-gray-50'
        },
        {
            id: 'resolved',
            title: 'Completed',
            amount: resolvedInsights.length,
            leftProgressBarClassName: selectedTopBarOption === 'resolved' ? 'bg-sf-primary-500' : 'bg-sf-primary-100',
            rightProgressBarClassName: selectedTopBarOption === 'resolved' ? 'bg-sf-gray-400' : 'bg-sf-gray-50'
        }
    ];

    const handleCreatePolicyClick = (insightId: PolicyInsight['id']) => {
        const insight = insights.find((insight) => insight.id === insightId);
        if (!insight) return;
        window.open(`/policy/templates/${insight.template.typeId}/create/${insight.template.id}?fromInsightId=${insight.id}`, '_blank');
    };

    const handleViewPolicyClick = (insightId: PolicyInsight['id']) => {
        const insight = insights.find((insight) => insight.id === insightId);
        if (!insight?.policyId) return;
        window.open(`/policy#${insight.policyId}`, '_blank');
    };

    const handleResolveInsightClick = (insightId: PolicyInsight['id']) => resolveInsight(insightId);

    const handleReopenInsightClick = (insightId: PolicyInsight['id']) => unresolveInsight(insightId);

    return (
        <div className="min-w-[290px] w-[290px] h-full bg-white overflow-hidden flex flex-col border-x border-sf-gray-100">
            <div className="px-4 py-2 flex gap-2 items-center border-b border-sf-gray-100 h-[49px]">
                <div className="flex items-center gap-1">
                    <Icon icon={Icons.Lightning} className="text-sf-gray-400" />
                    <span className="font-sf text-13 font-medium">Insights</span>
                </div>
                <Badge text={insights.length.toString()} color="secondary" />
            </div>
            <InsightsTopBar
                options={topBarOptions}
                selectedOption={selectedTopBarOption}
                totalInsightsAmount={insights.length}
                onOptionClick={setSelectedTopBarOption}
            />
            <div className="w-full flex-1 overflow-hidden">
                {insightsToDisplay.length ? (
                    <InsightsList
                        insights={insightsToDisplay}
                        insightsConfig={insightsConfig}
                        canCreatePolicy={canEditPolicies}
                        onViewPolicyClick={handleViewPolicyClick}
                        onCreatePolicyClick={handleCreatePolicyClick}
                        onResolveInsightClick={handleResolveInsightClick}
                        onReopenInsightClick={handleReopenInsightClick}
                    />
                ) : (
                    <EmptyState
                        className="w-full h-full border border-sf-gray-100 rounded-[8px]"
                        {...(selectedTopBarOption === 'resolved'
                            ? RESOLVED_INSIGHTS_EMPTY_STATE_PROPS
                            : OPEN_INSIGHTS_EMPTY_STATE_PROPS)}
                    />
                )}
            </div>
        </div>
    );
};

const PolicyInsights: FC<Props> = ({ insights, onInsightsUpdate, showLoading = false, showError = false }) => {
    const {
        data: insightsConfig,
        isLoading: loadingInsightsConfig,
        isError: hasInsightsConfigError
    } = usePolicyInsightsConfig();
    const { userSession, loading: loadingUserSession, error: userSessionError } = useGetUserData();

    if (showLoading || loadingInsightsConfig || loadingUserSession) {
        return <PolicyInsightsLoading />;
    }

    if (showError || hasInsightsConfigError || !!userSessionError) {
        return <PolicyInsightsErrorPlaceholder />;
    }

    if (!insightsConfig || !userSession) {
        return null;
    }

    return (
        <InsightsLayout
            insights={insights}
            insightsConfig={insightsConfig}
            userSession={userSession}
            onInsightsUpdate={onInsightsUpdate}
        />
    );
};

export default PolicyInsights;
